import { useState, useRef, useEffect } from "react";
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useDempStore } from "src/context/stores";
import { cn } from 'src/lib/utils';

import { useGetListCompany, createYearGroup, createItem, useGetListItems, deleteItem as deleteItemApi, updateItem, deleteGroup as deleteGroupApi } from "../../services/useEsgApi";


const sdGoals = [
    { id: 1, name: "No Poverty" },
    { id: 2, name: "Zero Hunger" },
    { id: 3, name: "Good Health and Well-being" },
    { id: 4, name: "Quality Education" },
    { id: 5, name: "Gender Equality" },
    { id: 6, name: "Clean Water and Sanitation" },
    { id: 7, name: "Affordable and Clean Energy" },
    { id: 8, name: "Decent Work and Economic Growth" },
    { id: 9, name: "Industry, Innovation and Infrastructure" },
    { id: 10, name: "Reduced Inequalities" },
    { id: 11, name: "Sustainable Cities and Communities" },
    { id: 12, name: "Responsible Consumption and Production" },
    { id: 13, name: "Climate Action" },
    { id: 14, name: "Life Below Water" },
    { id: 15, name: "Life on Land" },
    { id: 16, name: "Peace, Justice and Strong Institutions" },
    { id: 17, name: "Partnerships for the Goals" }
];

const getSubOptions = (esgType) => {
    switch(esgType) {
        case 'Environment':
            return [
                { value: 'E1', label: 'หมวดการจัดการสิ่งแวดล้อม' },
                { value: 'E2', label: 'หมวดการใช้ทรัพยากรอย่างมีประสิทธิภาพ' },
                { value: 'E3', label: 'หมวดการจัดการด้านสภาพภูมิอากาศ' }
            ];
        case 'Social':
            return [
                { value: 'S1', label: 'หมวดการเคารพสิทธิมนุษยชนและการปฎิบัติต่อแรงงานอย่างเป็นธรรม' },
                { value: 'S2', label: 'หมวดการพัฒนาศักยภาพแก่พนักงาน' },
                { value: 'S3', label: 'หมวดการจูงใจและรักษาพนักงาน' },
                { value: 'S4', label: 'หมวดสุขภาวะและความปลอดภัยในสภาพแวดล้อมการทำงาน' },
                { value: 'S5', label: 'หมวดการมีส่วนร่วมกับชุมชนและสังคม' },
                { value: 'S6', label: 'หมวดการมีส่วนร่วมของผู้มีส่วนได้เสีย' }
            ];
        case 'Governance & Economic':
            return [
                { value: 'G1', label: 'หมวดบรรษัทภิบาล' },
                { value: 'G2', label: 'หมวดจรรยาบรรณธุรกิจ ( Code of Conduct ) และการต่อต้านการทุจริต' },
                { value: 'G3', label: 'หมวดการระบุประเด็นที่เป็นสาระสำคัญของธุรกิจ ( Materiality )' },
                { value: 'G4', label: 'หมวดการบริหารความเสี่ยงและภาวะวิกฤต' },
                { value: 'G5', label: 'หมวดการบริหารจัดการความสัมพันธ์กับลูกค้า' },
                { value: 'G6', label: 'หมวดการบริหารจัดการห่วงโซ่อุปทาน' },
                { value: 'G7', label: 'หมวดการดำเนินการด้านภาษี' },
                { value: 'G8', label: 'หมวดนวัตกรรมทางธุรกิจเพื่อสังคมและ/หรือสิ่งแวดล้อม' },
                { value: 'G9', label: 'หมวดความปลอดภัยของข้อมูลและระบบสนเทศ' },
                { value: 'G10', label: 'หมวดการบริหารคุณภาพการให้บริการ' }
            ];
        default:
            return [];
    }
};

const AddItemPopup = ({ isOpen, onClose, groupYear, editItem = null, setSelectedYear }) => {
    const companyId = useDempStore((state) => state.companyId);
    const [error, setError] = useState(null);
    
    const { control, handleSubmit, register, formState: { errors }, watch, setValue, reset } = useForm({
        defaultValues: editItem || {
            esgType: '',
            subType: '',
            score: '',
            suggestion: '',
            sdGoal: [],
            griStandard: '',
            topic_list: [{measurement: '', target: '', target_and_achieved: '', operation_team: ''}]
        },
        mode: 'onChange'
    });

    const { listItems } = useGetListItems(companyId, groupYear);

    useEffect(() => {
        if (editItem) {
            if (editItem.sdGoal && !Array.isArray(editItem.sdGoal)) {
                editItem.sdGoal = [editItem.sdGoal];
            }
         
            const formattedEditItem = {
                ...editItem,
                griStandard: Array.isArray(editItem.griStandard) 
                    ? editItem.griStandard.join(', ')
                    : editItem.griStandard || ''
            };
            reset(formattedEditItem);
        }
    }, [editItem, reset]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'topic_list'
    });

    const handleESGTypeChange = (value) => {
        setValue('esgType', value);
        setValue('subType', ''); 
    };

    const onSubmit = async (data) => {
        setError(null);
        
        try {
            const processedData = {
                ...data,
                sdGoal: Array.isArray(data.sdGoal) 
                    ? [...data.sdGoal].sort((a, b) => a - b) 
                    : data.sdGoal,
                griStandard: data.griStandard 
                    ? data.griStandard.split(',').map(item => item.trim()).filter(item => item !== '')
                    : []
            };

            
            if (editItem) {
                const apiData = {
                    data:  processedData
                };

                const response = await updateItem(apiData, companyId, groupYear, editItem.id);
                
                if (response.error) {
                    setError("Failed to update item. Please try again.");
                    console.error("API Error:", response.error);
                } else {
                    setSelectedYear(prev => prev.filter(y => y !== groupYear));
                    setTimeout(() => setSelectedYear(prev => [...prev, groupYear].sort((a, b) => a - b)), 100);
                    onClose();
                }
            } else {
                const response = await createItem(processedData, companyId, groupYear);
                
                if (response.error) {
                    setError("Failed to create item. Please try again.");
                    console.error("API Error:", response.error);
                } else {
                    setSelectedYear(prev => prev.filter(y => y !== groupYear));
                    setTimeout(() => setSelectedYear(prev => [...prev, groupYear].sort((a, b) => a - b)), 100);
                    onClose();
                }
            }
        } catch (err) {
            setError("An unexpected error occurred. Please try again.");
            console.error("Error processing item:", err);
        } 
    };

    if (!isOpen) return null;

    const overviewQuestion = [
        { id: 'policy', title: 'Policy' },
        { id: 'disclosure', title: 'Disclosure' },
        { id: 'target', title: 'Target' },
        { id: 'performance', title: 'Performance' },
        { id: 'implementation', title: 'Implementation' }
    ]

    const fieldQuestion = [
        { id: 'companyPotentialOperation', label: "Company's Potential Operation" },
        { id: 'industrialOperation', label: "Industrial Operation" },
        { id: 'companyOperation', label: "Company's Operation" }
    ]

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#79C8B0] to-[#1FA37C] flex items-center justify-center">
                                <span className="text-2xl font-bold text-white">+</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">{groupYear} | {editItem ? 'Edit Report' : 'Add Report'}</h3>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

               
                <div className="flex-1 overflow-y-auto p-6">
                    <form onSubmit={handleSubmit(onSubmit)} id="itemForm" className="space-y-8">
                       
                        <section className="bg-white rounded-xl border p-6 space-y-6">
                            <h4 className="text-lg font-semibold text-gray-800">ESG Information</h4>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">ESG Type</label>
                                    <Controller
                                        name="esgType"
                                        control={control}
                                        render={({ field }) => (
                                            <select 
                                                {...field}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-200 transition-all"
                                                onChange={(e) => handleESGTypeChange(e.target.value)}
                                            >
                                                <option value="">Please select ESG</option>
                                                <option value="Environment">Environment</option>
                                                <option value="Social">Social</option>
                                                <option value="Governance & Economic">Governance & Economic</option>
                                            </select>
                                        )}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">Sub Type</label>
                                    <Controller
                                        name="subType"
                                        control={control}
                                        render={({ field }) => {
                                            const esgType = watch('esgType');
                                            const options = getSubOptions(esgType);
                                            let usedSubTypes = [];
                                            if (listItems?.data?.[groupYear]?.items) {
                                                usedSubTypes = listItems.data[groupYear].items
                                                    .filter(item => item.esgType === esgType && (!editItem || item.id !== editItem.id))
                                                    .map(item => item.subType);
                                            }
                                            return (
                                                <select 
                                                    {...field}
                                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-200 transition-all disabled:bg-gray-100"
                                                    disabled={!esgType}
                                                >
                                                    <option value="">Select sub-type</option>
                                                    {options.map(option => (
                                                        <option key={option.value} value={option.value} disabled={usedSubTypes.includes(option.value)}>
                                                            {/* {option.label} */}
                                                            {option.label} {usedSubTypes.includes(option.value) ? '(ถูกเลือกแล้ว)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            );
                                        }}
                                    />
                                </div>
                            </div>

                            {watch('esgType') && watch('subType') && (
                                <div className="space-y-6 mt-6">
                                    <h4 className="text-lg font-semibold text-gray-800">
                                        {watch('esgType') === "Environment" 
                                            ? "Environment Score Evaluation" 
                                            : watch('esgType') === "Social" 
                                                ? "Social Score Evaluation" 
                                                : "Governance Score Evaluation"}
                                    </h4>
                                    <div className="space-y-6">
                                        <div className="space-y-4">
                                            <label className="block text-sm font-medium text-gray-700">Evaluated Score</label>
                                            <Controller
                                                name="score"
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-6">
                                                        <input 
                                                            type="range" 
                                                            min="0" 
                                                            max="100" 
                                                            {...field}
                                                            value={field.value || '0'}
                                                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                                            onChange={(e) => field.onChange(e.target.value)}
                                                        />
                                                        {/* <span className="w-16 text-center font-semibold text-lg text-blue-600">
                                                            {field.value || '0'}
                                                        </span> */}
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            value={field.value || '0'}
                                                            onChange={(e) => {
                                                                const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                                                field.onChange(value.toString());
                                                            }}
                                                            className="w-20 p-2 border rounded-lg text-center font-semibold text-lg text-blue-600 focus:ring-2 focus:ring-blue-200"
                                                        />
                                                    </div>
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">Suggestion Details</label>
                                            <Controller
                                                name="suggestion"
                                                control={control}
                                                render={({ field }) => (
                                                    <textarea 
                                                        {...field} 
                                                        className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-200 transition-all" 
                                                        rows="3" 
                                                        placeholder="Enter your suggestion details here..."
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </section>

                        <section className="bg-white rounded-xl border p-6 space-y-6">
                            <h4 className="text-lg font-semibold text-gray-800">Sustainable Development Goals</h4>
                            <Controller
                                name="sdGoal"
                                control={control}
                                render={({ field }) => (
                                    <div className="bg-[#e8f5e9] p-6 rounded-xl border">
                                        <div className="grid grid-cols-8 gap-4">
                                            {sdGoals.map((goal) => {
                                                const isSelected = Array.isArray(field.value) && field.value.includes(goal.id);
                                                return (
                                                    <div 
                                                        key={goal.id}
                                                        className={`relative cursor-pointer transition-transform hover:scale-105 ${
                                                            isSelected ? 'ring-4 ring-vekin rounded-lg shadow-lg scale-105' : ''
                                                        }`}
                                                        onClick={() => {
                                                            if (Array.isArray(field.value)) {
                                                                if (isSelected) {
                                                                    field.onChange(field.value.filter(id => id !== goal.id));
                                                                } else {
                                                                    field.onChange([...field.value, goal.id]);
                                                                }
                                                            } else {
                                                                field.onChange([goal.id]);
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={`https://sdgs.un.org/sites/default/files/goals/E_SDG_Icons-${String(goal.id).padStart(2, '0')}.jpg`}
                                                            alt={`SDG ${goal.id}`}
                                                            className="w-full rounded-lg shadow-sm"
                                                        />
                                                        {isSelected && (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                                                                <div className="bg-vekin text-white p-2 rounded-full">
                                                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            />
                        </section>

                     
                        <section className="bg-white rounded-xl border p-6 space-y-6">
                            <h4 className="text-lg font-semibold text-gray-800">GRI Standards</h4>
                            <div className="space-y-2">
                                <p className="text-sm text-gray-600">Enter multiple GRI standards separated by commas (e.g. "GRI 102, GRI 103, GRI 405")</p>
                                <Controller
                                    name="griStandard"
                                    control={control}
                                    render={({ field }) => (
                                        <textarea 
                                            {...field} 
                                            className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-200 transition-all" 
                                            rows="3" 
                                            placeholder="Enter GRI standards separated by commas..."
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                            value={Array.isArray(field.value) ? field.value.join(', ') : field.value}
                                        />
                                    )}
                                />
                            </div>
                        </section>

                       
                        <section className="bg-white rounded-xl border p-6 space-y-6">
                            <div className="flex items-center justify-between">
                                <h4 className="text-lg font-semibold text-gray-800">Topic List</h4>
                                <button
                                    type="button"
                                    onClick={() => append({ measurement: '', target: '', target_and_achieved: '', operation_team: '' })}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#79C8B0] to-[#1FA37C] rounded-lg hover:opacity-90 transition-all"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Add Topic
                                </button>
                            </div>
                            
                            <div className="space-y-4">
                                {fields.map((item, index) => (
                                    <div key={item.id} className="bg-gray-50 rounded-xl p-6 relative">
                                        <div className="absolute left-0 top-0 w-8 h-8 bg-gradient-to-r from-[#79C8B0] to-[#1FA37C] flex items-center justify-center text-white font-bold rounded-tl-lg rounded-br-lg">
                                            {index + 1}
                                        </div>
                                        
                                        <button
                                            type="button"
                                            onClick={() => remove(index)}
                                            className={cn(
                                                "absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors",
                                                fields.length <= 1 ? "hidden" : ""
                                            )}
                                        >
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>

                                        <div className="grid grid-cols-2 gap-6 mt-4 pl-4">
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-600">Measuring Indicator</label>
                                                <Controller
                                                    name={`topic_list.${index}.measurement`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <textarea {...field} type="text" className="w-full p-3 border rounded-lg" placeholder="Enter measuring indicator..." />
                                                    )}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-600">Target</label>
                                                <Controller
                                                    name={`topic_list.${index}.target`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <textarea {...field} type="text" className="w-full p-3 border rounded-lg" placeholder="Enter target..." />
                                                    )}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-600">Achievement</label>
                                                <Controller
                                                    name={`topic_list.${index}.target_and_achieved`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <textarea {...field} type="text" className="w-full p-3 border rounded-lg" placeholder="Enter achievement..." />
                                                    )}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-600">Operation Team</label>
                                                <Controller
                                                    name={`topic_list.${index}.operation_team`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <textarea {...field} type="text" className="w-full p-3 border rounded-lg" placeholder="Enter operation team..." />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        
                        
                        {error && (
                            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">
                                {error}
                            </div>
                        )}
                    </form>
                </div>

               
                <div className="border-t p-6 flex justify-end gap-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-lg hover:opacity-90 transition-all font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="itemForm"
                        className="px-6 py-2.5 bg-gradient-to-r from-[#79C8B0] to-[#1FA37C] text-white rounded-lg hover:opacity-90 transition-all font-medium"
                    >
                        {editItem ? 'Update' : 'Submit'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddItemPopup;